import * as Enums from '../../enums/index.js';

class MlbLineupConverter {
    constructor(dateCreated) {
        const currentDate = dateCreated || new Date();
        this.template = {
            // TODO - enum
            // @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-lineup-v2.json
            id: null,
            sessionID: null,
            type: null,
            teamID: null,
            rosterID: null,
            dateCreated: currentDate.toISOString(),
            ts: currentDate.toISOString(),
            battingOrder: [],
            bench: [],
            substitutions: [],
            switches: [],
        };

        this.battingOrderTemplate = {
            // @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/lineup-tag.json
            // sessionLineUpID: null,
            // playerIndex: null,
            position: null,
            tagID: null,
            height: null,
            replacedFielderId: null,
            replacedFielderPosition: null
        };

        this.benchPlayerTemplate = {
            // @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/bench-tag.json
            tagID: null,
            position: null,
            height: null,
        };

        this.substitutionTemplate = {
            // @see https://bitbucket.org/flightscope/dto-schema/src/1.0.9/baseball/entity/player-substitution.json
            enteredTagID: null,
            replacedTagID: null,
            type: null,
            enteredDate: null,
        };

        this.positionSwitchTemplate = {
            // @see https://bitbucket.org/flightscope/dto-schema/src/1.0.9/baseball/entity/position-switch.json
            fielderATagID: null,
            fielderBTagID: null,
            switchDate: null,
        };
    }

    createLineupTemplate() {
        const template = Object.assign({}, this.template);
        // we need to loose reference to array, otherwise it can get messy
        template.battingOrder = [];
        template.bench = [];
        template.substitutions = [];
        template.switches = [];

        return template;
    }

    convertLineups(game, sessionId, homeTeamId, awayTeamId) {
        // TODO - entity
        if (!game || typeof(game) !== 'object') {
            throw new TypeError('game must be an object');
        }

        const away = this.createLineupTemplate();
        const home = this.createLineupTemplate();

        away.sessionID = sessionId;
        home.sessionID = sessionId;
        away.type = 'away';
        home.type = 'home';

        if (game.gameData) {
            if (game.gameData.teams) {
                const awayTeam = game.gameData.teams.away;
                const homeTeam = game.gameData.teams.home;
    
                away.teamID = awayTeamId || awayTeam.id;
                away.id = sessionId * 10 + 1;
                home.teamID = homeTeamId || homeTeam.id;
                home.id = sessionId * 10 + 2;
            }
        }

        if (game.liveData && game.liveData.boxscore && game.liveData.boxscore.teams) {
            // we can build batting order based on final state and then look back at switches and substitutions in order to re-create initial batting order and bench
            if (game.liveData.boxscore.teams.away) {
                this.reproduceFinalBattingOrder(away, game.liveData.boxscore.teams.away, game.gameData.players);
            }
            if (game.liveData.boxscore.teams.home) {
                this.reproduceFinalBattingOrder(home, game.liveData.boxscore.teams.home, game.gameData.players);
            }
        }

        // let's analyze plays from the end
        if (game.liveData && game.liveData.plays && game.liveData.plays.allPlays) {
            for (let i = game.liveData.plays.allPlays.length - 1; i >= 0; i--) {
                const play = game.liveData.plays.allPlays[i];
                if (!play) {
                    continue;
                }

                // TODO - enum
                // now look for substitutions during this play
                if (play.about.halfInning === 'top') {
                    this.analyzePlayEvents(play.playEvents, away, game.liveData.boxscore.teams.away, home, game.liveData.boxscore.teams.home);
                }
                else {
                    this.analyzePlayEvents(play.playEvents, home, game.liveData.boxscore.teams.home, away, game.liveData.boxscore.teams.away);
                }
            }
        }

        return [away, home];
    }

    getPlayerIndexProperty(id) {
        return `ID${id}`;
    }

    reproduceFinalBattingOrder(lineup, boxScore, players) {
        // look for players in most recent batting order
        for (let i = 0; i < boxScore.battingOrder.length; i++) {
            const playerId = boxScore.battingOrder[i];
            
            const batterData = boxScore.players[this.getPlayerIndexProperty(playerId)];
            if (!batterData) {
                throw new Error(`unable to find player data in box score for batter ${playerId}`);
            }

            const battingOrder = Object.assign({}, this.battingOrderTemplate);
            // note box score contains final position in game (after switches)
            battingOrder.position = batterData.position.abbreviation || players[this.getPlayerIndexProperty(playerId)].primaryPosition.abbreviation;

            if (battingOrder.position === 'DH') {
                // check who is replaced during fielding,
                // TODO - separate method
                if (!boxScore.pitchers || !boxScore.pitchers.length) {
                    throw new Error(`Unable to determine pitcher for DH ${playerId}`);
                }
                const lastPitcherId = boxScore.pitchers[boxScore.pitchers.length - 1];
                if (!lastPitcherId) {
                    throw new Error(`Unable to determine pitcher for DH ${playerId}`);
                }

                // TODO - couldn't find better way to do it
                battingOrder.replacedFielderId = lastPitcherId;
                const fielderData = boxScore.players[this.getPlayerIndexProperty(lastPitcherId)];
                if (!fielderData) {
                    throw new Error(`Unble to find player data in boxscore, id ${lastPitcherId}`);
                }

                battingOrder.replacedFielderPosition = fielderData.position.abbreviation;
                // check if he is on bench
                if (lineup.bench.findIndex(bo => bo.tagID === lastPitcherId) === -1) {
                    const fielerBench = Object.assign({}, this.benchPlayerTemplate);
                    fielerBench.tagID = lastPitcherId;
                    fielerBench.position = fielderData.position.abbreviation;
                    // TODO - height
                    lineup.bench.push(fielerBench);
                }
            }

            battingOrder.tagID = playerId;
            //battingOrder.playerIndex = teamAtBat.battingOrder.length;
            //battingOrder.sessionLineUpID = teamAtBat.id;
            // TODO - height
            lineup.battingOrder.push(battingOrder);
        }
    }

    analyzePlayEvents(playEvents, teamAtBat, boxScoreAtBat, teamFielding, boxScoreFielding, pitcher) {
        for (let pe = playEvents.length - 1; pe >= 0; pe--) {
            const playEvent = playEvents[pe];

            if (!playEvent.isSubstitution) {
                continue;
            }

            const substitutionType = playEvent.details.eventType;
            switch (substitutionType) {
                case 'defensive_substitution':
                    this.handleSubstitution(
                        playEvent, teamFielding, boxScoreFielding,
                        Enums.SubstitutionType.DefensiveSubstitute.key
                    );
                    break;

                case 'pitching_substitution':
                    this.handleSubstitution(
                        playEvent, teamFielding, boxScoreFielding,
                        Enums.SubstitutionType.PitchingChange.key,
                        pitcher
                    );
                    break;

                case 'offensive_substitution':
                    this.handleSubstitution(
                        playEvent, teamAtBat, boxScoreAtBat,
                        playEvent.position.abbreviation // PH or PR...
                    );
                    break;

                case 'defensive_switch':
                    this.handlePositionSwitch(
                        playEvent, teamFielding
                    );
                    break;

                default:
                    throw new Error(`Unsupported type of substitution at playEvent from ${playEvent.startTime} - ${substitutionType} `)
            }
        }
    }

    getFieldersAtPosition(lineup, positionCode) {
        const fielder = lineup.battingOrder.find(bo => bo.position === positionCode);
        // TODO - enum for position
        if (fielder) {
            return fielder.tagID;
        }
        if (positionCode === 'P') {
            // look also for DH
            const dh = lineup.battingOrder.find(bo => bo.position === 'DH');
            if (dh && dh.replacedTagID && dh.replacedFielderPosition === 'P') {
                return dh.replacedTagID;
            }
        }

        return null;
    }

    handlePositionSwitch(playEvent, lineup) {
        const { player, replacedPlayer, startTime } = playEvent;

        // add defensive position switch
        const positionSwitch = Object.assign({}, this.positionSwitchTemplate);
        positionSwitch.fielderBTagID = player.id;
        if (replacedPlayer) {
            if (lineup.battingOrder.findIndex(bo => bo.tagID === replacedPlayer.id) === -1) {
                // TODO - this will usually happen when we have a defensive switch after a substitution, don't know why but MLB reports it on the next inning (?)
                // in this case we just need to switch players... that's it, do not rely on replacedPlayer information (as his substituion has been already handled and he is not in the batting order since then)
                const possibleSwitchedWith = this.getFieldersAtPosition(
                    lineup, playEvent.position.abbreviation
                );
    
                if (!possibleSwitchedWith) {
                    throw new Error(`Unable to handle position switch from ${startTime} - couldn't find player on position ${playEvent.position.abbreviation}`);
                }
    
                positionSwitch.fielderATagID = possibleSwitchedWith;
            }
            else {
                positionSwitch.fielderATagID = replacedPlayer.id;
            }
        }
        else {
            // TODO - I don't know why, but sometimes we have no information about replaced player...
            // in this case we need to extract position from the description, again, not the best way, but can't do it differently
            // description: 'Defensive switch from center field to left field for Guillermo Heredia.'
            // @see https://regex101.com/r/1ra3w4/1
            const positionMatch = /[\s]from[\s](.+)[\s]to[\s]/.exec(playEvent.details.description);
            if (!positionMatch || !positionMatch[1]) {
                throw new Error(`Unable to handle position switch from ${startTime} - could't determine initial position, description: ${playEvent.details.description}`);
            }

            const initialPosition = this.mapPositionName(positionMatch[1]);
            if (!initialPosition) {
                throw new Error(`Unable to handle position switch from ${startTime} - could't map initial position: ${positionMatch[1]}`);
            }

            const possibleSwitchedWith = this.getFieldersAtPosition(
                lineup, initialPosition
            );

            if (!possibleSwitchedWith) {
                throw new Error(`Unable to handle position switch from ${startTime} - couldn't find player on position ${initialPosition}`);
            }

            positionSwitch.fielderATagID = possibleSwitchedWith;
        }

        positionSwitch.switchDate = startTime;
        lineup.switches.unshift(positionSwitch);

        // now let's apply the same change to the lineup, this is necessary as we are aanalyzing the feed from the end, so position swithc needs to be reverted
        const battingOrderAIndex = lineup.battingOrder.findIndex(bo => bo.tagID === positionSwitch.fielderATagID);
        const battingOrderBIndex = lineup.battingOrder.findIndex(bo => bo.tagID === positionSwitch.fielderBTagID);
        // so now we need to apply this switch to the batting order
        const playerAPosition = lineup.battingOrder[battingOrderAIndex].position;
        const playerBPosition = lineup.battingOrder[battingOrderBIndex].position;
        lineup.battingOrder[battingOrderBIndex].position = playerAPosition;
        lineup.battingOrder[battingOrderAIndex].position = playerBPosition;
    }

    handleSubstitution(playEvent, lineup, boxScore, substitutionType, pitcher) {
        const { player, replacedPlayer, startTime } = playEvent;
        let replacedPlayerData = null;
        if (!replacedPlayer) {
            // in case of pitching substitution we don't have this information in MLB feed...
            if (substitutionType !== Enums.SubstitutionType.PitchingChange.key) {
                throw new Error(`Unable to handle substitution from ${startTime} as information about replaced player is not provided in feed`);
            }

            // TODO - currently I can't find better way to find replaced pitcher than parsing the substitution description
            // description:'Pitching Change: John Nogowski replaces Duane Underwood, batting 5th.'
            // @see https://regex101.com/r/qHi3mR/1
            const pitcherNameMatch = /:[\s].+[\s]replaces[\s](.+),/.exec(playEvent.details.description);
            if (!pitcherNameMatch || !pitcherNameMatch[1]) {
                throw new Error(`Unable to handle substitution from ${startTime} - could't determine replaced pitcher name, description: ${playEvent.details.description}`);
            }

            const pitcherName = pitcherNameMatch[1];
            // TODO - using indexOf based matching as fullName for Duane Underwood is Duane Underwood Jr. 
            // It could be improved by passing full set of players from .gameData and looking for a player using first and last name separately...
            const pitcherData = Object.values(boxScore.players).find(p => p.person.fullName.indexOf(pitcherName) !== -1);
            if (!pitcherData) {
                throw new Error(`Unable to handle substitution from ${startTime} - could't determine replaced pitcher using name ${pitcherName}`);
            }

            replacedPlayerData = boxScore.players[this.getPlayerIndexProperty(pitcherData.person.id)];
        }
        else {
            replacedPlayerData = boxScore.players[this.getPlayerIndexProperty(replacedPlayer.id)];
        }

        // so now we need to apply changes to the lineup, start from batting order
        // please keep in mind that we are going backward, so batting order is after the substitution, we need to revert it
        const battingIndex = lineup.battingOrder.findIndex(bp => bp.tagID === player.id);
        // look for DH case...
        const dhIndex = lineup.battingOrder.findIndex(bp => bp.replacedFielderId === player.id);

        if (dhIndex !== -1) {
            lineup.battingOrder[dhIndex].replacedFielderId = replacedPlayerData.person.id;;
        }
        else if (battingIndex !== -1) {
            lineup.battingOrder[battingIndex].tagID = replacedPlayerData.person.id;
        }
        else {
            throw new Error(`Unable to handle substitution from ${startTime}, unable to find player ${player.id} in bench`);
        }

        // put replaced player to the bench if not here already
        const benchIndex = lineup.bench.findIndex(bp => bp.tagID === player.id);
        if (benchIndex === -1) {
            const benchPlayer = Object.assign({}, this.benchPlayerTemplate);
            benchPlayer.tagID = player.id;
            benchPlayer.position = replacedPlayerData.position.abbreviation;
            // TODO - height
            lineup.bench.unshift(benchPlayer);
        }

        // add substitution
        const substitution = Object.assign({}, this.substitutionTemplate);
        substitution.type = substitutionType;
        substitution.enteredTagID = player.id;
        substitution.replacedTagID = replacedPlayerData.person.id;
        substitution.enteredDate = startTime;

        lineup.substitutions.unshift(substitution);
    }

    mapPositionName(position) {
        // TODO - separate mapper
        switch(position.trim().toLowerCase()) {
            case 'center field':
                return Enums.LineupPosition.CenterFielder.key;
            case 'left field':
                return Enums.LineupPosition.LeftFielder.key;
            case 'right field':
                return Enums.LineupPosition.RightFielder.key;
            case 'first base':
                return Enums.LineupPosition.FirstBaseman.key;
            case 'second base':
                return Enums.LineupPosition.SecondBaseman.key;
            case 'third base':
                return Enums.LineupPosition.ThirdBaseman.key;
            case 'short stop':
            case 'shortstop':
                return Enums.LineupPosition.ShortStop.key;
            case 'pitcher':
                return Enums.LineupPosition.Pitcher.key;
            case 'catcher':
                return Enums.LineupPosition.Catcher.key;
        }

        return null;
    }
}

export default MlbLineupConverter;